import axios from 'axios';

const resetAvailableEntities = async ({debug, baseUri, env, apiKey}) => {
  const urlObj = new URL(`${baseUri}/${env}/resetAvailableEntities`);

  const { data } = await axios.delete(
    urlObj,
    { headers: { 'x-api-key': apiKey, 'Content-type': 'application/json' } }
  );
  debug({ data });
};

const genCredentialsOnClientApi = async ({ debug, baseUri, task, env, apikey, ...params }) => {
  const urlObj = new URL(`${baseUri}/credentials/${task}/${env}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value);
    });
  }
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers: { 'x-api-key': apikey } });
  const { accessKeyId, secretAccessKey, sessionToken } = data;
  return { accessKeyId, secretAccessKey, sessionToken };
};

const getTenants = async ({ debug, baseUri, env, apikey }) => {
  const uri = `${baseUri}/tenants/${env}`;
  debug('requesting:', uri);
  const { data } = await axios.get(uri, { headers: { 'x-api-key': apikey } });
  debug('response-data', data);
  return data;
};

const getSupportedSources = async ({ debug, baseUri, env, flow, apikey }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/supportedSources`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const getSupportedTargets = async ({ debug, baseUri, env, flow, apikey }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/supportedTargets`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const getSupportedConnectors = async ({ debug, baseUri, env, flow, apikey }) => {
  const urlObj = new URL(`${baseUri}/v2/${env}/${flow}/supportedConnectors`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const getLinkedSources = async ({ debug, baseUri, env, flow, tenant, apikey, config }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${tenant}/linkedSources`);
  if (config) {
    urlObj.searchParams.set('config', 'true');
  }
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const getLinkedConnectors = async ({ debug, baseUri, env, flow, tenant, apikey, config }) => {
  const urlObj = new URL(`${baseUri}/v2/${env}/${flow}/${tenant}/linkedConnectors`);
  if (config) {
    urlObj.searchParams.set('config', 'true');
  }
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const patchLinkedSourceConfig = async ({ debug, baseUri, env, flow, tenant, apikey, connectorId, config }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${tenant}/linkedSources`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  const body = { tap: connectorId, config };
  debug && debug('uri:', uri);
  const { data } = await axios.patch(uri, body, { headers });
  return data;
};

const patchLinkedConnectorConfig = async ({ debug, baseUri, env, flow, tenant, apikey, connectorId, config }) => {
  const urlObj = new URL(`${baseUri}/v2/${env}/${flow}/${tenant}/linkedConnectors`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  const body = { connector_id: connectorId, config };
  debug && debug('uri:', uri);
  const { data } = await axios.patch(uri, body, { headers });
  return data;
};

const getSupportedFlows = async ({ debug, baseUri, env, apikey }) => {
  const urlObj = new URL(`${baseUri}/${env}/flows/supported`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const getSupportedFlow = async ({ debug, baseUri, env, flow, apikey }) => {
  const urlObj = new URL(`${baseUri}/${env}/flows/${flow}/supported`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const deleteTenantSchedules = async ({ debug, baseUri, env, apikey, tenant }) => {
  const urlObj = new URL(`${baseUri}/tenant/${env}/${tenant}?schedules_only=true`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.delete(uri, { headers });
  return data;
};

const runJob = async ({ debug, baseUri, env, apikey, tenant, flow, ...args }) => {
  const { isV2Flow = false, ...body } = args;
  const urlObj = new URL(`${baseUri}/${isV2Flow ? 'v2/' : ''}${env}/${flow}/${tenant}/jobs`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.post(uri, body, { headers });
  return data;
};

const getEnvJobs = async ({ debug, baseUri, env, apikey, from, to, count = 10000, ...queryParams }) => {
  const headers = { 'x-api-key': apikey };
  const countNum = typeof count === 'string' ? parseInt(count, 10) : count;
  let allJobs = [];
  let totalJobs = 0;
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const urlObj = new URL(`${baseUri}/${env}/jobs`);
    urlObj.searchParams.set('from', from);
    if (to) {
      urlObj.searchParams.set('to', to);
    }
    urlObj.searchParams.set('count', countNum.toString());
    urlObj.searchParams.set('page', page.toString());
    
    // Add any additional query parameters
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'count' && key !== 'page') {
          urlObj.searchParams.set(key, value);
        }
      });
    }

    urlObj.searchParams.set('include_export_details', 'false');
    urlObj.searchParams.set('skip_cache', 'true');
    
    const uri = urlObj.toString();
    debug && debug(`Fetching page ${page}, uri:`, uri);
    const { data } = await axios.get(uri, { headers });
    debug && debug(`Page ${page} response:`, { jobsCount: data.jobs?.length || 0, total: data.total || 0 });
    
    const pageJobs = data.jobs || [];
    allJobs = allJobs.concat(pageJobs);
    
    // Set total from first page response
    if (page === 1) {
      totalJobs = data.total || 0;
    }
    
    // If we got fewer jobs than the count, we've reached the last page
    if (pageJobs.length < countNum) {
      hasMore = false;
    } else {
      page++;
    }
  }

  debug && debug(`Fetched ${allJobs.length} jobs across ${page} page(s)`);
  
  return {
    jobs: allJobs,
    total: totalJobs
  };
};

const retriggerJob = async ({ debug, baseUri, env, apikey, tenant, flow, ...args }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${tenant}/jobs/retrigger`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  
  const payload = args;
  
  debug && debug('uri:', uri);
  debug && debug('payload:', payload);
  const { data } = await axios.post(uri, payload, { headers });
  return data;
};

const getScheduleList = async ({ debug, baseUri, env, apikey, includeDisabled }) => {
  const urlObj = new URL(`${baseUri}/tenants/${env}/schedule`);
  if (includeDisabled !== undefined) {
    urlObj.searchParams.set('includeDisabled', includeDisabled === true ? 'true' : 'false');
  }
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const updateSchedule = async ({ debug, baseUri, env, flow, user_id, apikey, schedule_expression, state, job_type, connector_id, extra_args }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${user_id}/jobs/schedule`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  
  const body = {
    schedule_expression,
    state,
  };
  
  if (job_type) {
    body.job_type = job_type;
  }
  
  if (connector_id) {
    body.connector_id = connector_id;
  }
  
  if (extra_args) {
    Object.assign(body, extra_args);
  }
  
  debug && debug('uri:', uri);
  debug && debug('body:', body);
  const { data } = await axios.put(uri, body, { headers });
  return data;
};

const killJob = async ({ debug, baseUri, env, flow, user_id, apikey, job_root, reason, task_arn, skip_webhooks }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${user_id}/jobs/kill`);
  urlObj.searchParams.set('job_root', job_root);
  if (reason) {
    urlObj.searchParams.set('reason', reason);
  }
  if (task_arn) {
    urlObj.searchParams.set('task_arn', task_arn);
  }
  if (skip_webhooks) {
    urlObj.searchParams.set('skip_webhooks', skip_webhooks === true ? 'true' : 'false');
  }
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

const rollbackJob = async ({ debug, baseUri, env, flow, user_id, apikey, job_root, tap }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${user_id}/jobs/rollback`);
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  
  const body = {
    job_root,
    tap,
  };
  
  debug && debug('uri:', uri);
  debug && debug('body:', body);
  const { data } = await axios.post(uri, body, { headers });
  return data;
};

const getJobStatus = async ({ debug, baseUri, env, flow, user_id, apikey, job_root, include_export_details = false }) => {
  const urlObj = new URL(`${baseUri}/${env}/${flow}/${user_id}/jobs/status`);
  urlObj.searchParams.set('job_root', job_root);
  urlObj.searchParams.set('include_export_details', include_export_details ? 'true' : 'false');
  const headers = { 'x-api-key': apikey };
  const uri = urlObj.toString();
  debug && debug('uri:', uri);
  const { data } = await axios.get(uri, { headers });
  return data;
};

export {
  genCredentialsOnClientApi,
  getTenants,
  getSupportedSources,
  getSupportedConnectors,
  getLinkedSources,
  getLinkedConnectors,
  patchLinkedSourceConfig,
  patchLinkedConnectorConfig,
  getSupportedFlows,
  deleteTenantSchedules,
  getSupportedFlow,
  getSupportedTargets,
  resetAvailableEntities,
  runJob,
  retriggerJob,
  getEnvJobs,
  getScheduleList,
  updateSchedule,
  killJob,
  rollbackJob,
  getJobStatus,
};
